import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  EmptyState,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  IconTile,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NavPane,
  NavPaneContent,
  NavPaneGroup,
  NavPaneHeader,
  NavPaneItem,
  NavPaneTitle,
  Separator,
  Text,
  useShellTier,
  useWidgetRegistry,
  type IconName,
  type Color,
  type WidgetPreset,
} from "@salestrak/ui";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { TilePreview } from "#app/widgets/dashboards/settings/tile-preview";
import { widgetTagLabel } from "#app/widgets/dashboards/widget-tags";
// Direct module path (not the feature barrel): the barrel imports the Dashboard
// host → the app registry → definitions.
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import {
  featuredPresetKeys,
  isWidgetSection,
  orderSectionPresets,
  WIDGET_SECTION_ORDER,
  WIDGET_SECTIONS,
  type WidgetSection,
} from "#app/widgets/dashboards/widget-sections";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

const ACTIVATION_OFFSET = 24;

/** Nav-jump landing inset: matches the scroll column's `paddingTop="md"`, so
 *  a jumped-to section heading gets the same air above it the first section
 *  has at rest. Must stay under ACTIVATION_OFFSET or the scroll-spy would
 *  mark the previous section active after the jump settles. */
const SECTION_SCROLL_INSET = 16;

/** The nav pane on a tablet-width viewport: the tile column is the reason the
 *  dialog exists, and three tiles beside a 240px nav read as cramped strips
 *  until the shell reaches its full tier. 200 is the same trade the
 *  Integrations dialog makes, and the longest row label ("Embeds & apps")
 *  still sits on one line. */
const NAV_WIDTH_NARROW = 200;

/** Preset tiles: one column on phones, two while the shell is below its full
 *  tier, three from `xl` up — the same 1280 cut `useShellTier` calls `full`,
 *  so the third column arrives with the wider nav rather than squeezing three
 *  tiles into a tablet-width dialog. The library ships no grid primitive, so
 *  the layout is a class. */
const PRESET_GRID = "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3";

/** The gallery card's hover lift — Box carries no hover-border or transition
 *  props. */
const PRESET_CARD_HOVER =
  "text-start transition-colors hover:border-foreground/32";


/** The Custom gallery mixes hues per entry (reference parity); every other section stays on its own color. */
const CUSTOM_PRESET_HUES: readonly Color[] = [
  "violet",
  "orange",
  "teal",
  "blue",
  "pink",
  "yellow",
  "green",
  "red",
  "light-blue",
];

export type AddWidgetDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (preset: WidgetPreset) => void;
  /** Sidebar header label — the same gallery doubles as the Replace picker
   *  (the dashboard host swaps the copy while a replace is pending). */
  title?: string;
}>;

/**
 * One nav row and one content chapter. Assembled from the domain's section
 * registry (`widget-sections.ts`) — this file hand-lists nothing. A preset's own
 * catalog `section` decides where it lands, so shipping a widget files it, and
 * the dialog renders whichever sections actually hold something.
 */
type GallerySection = {
  key: WidgetSection;
  label: string;
  icon: IconName;
  color: Color;
};

/** Where the scroll-spy starts, and what it falls back to when a search empties
 *  the list — the first nav row, by construction. */
const DEFAULT_SECTION: WidgetSection = WIDGET_SECTION_ORDER[0];

/**
 * One gallery entry: a full-bleed gradient preview (dark mini-window mockup
 * per chart family) over a name + description footer. Pressing it adds the
 * widget.
 */
function PresetEntry({
  preset,
  color,
  onSelect,
  compact = false,
}: Omittable<{
  preset: WidgetPreset;
  color: Color;
  onSelect: (preset: WidgetPreset) => void;
  compact?: boolean;
}>) {
  return (
    // A pressable tile is a real button element, not a Card — the library Card
    // carries no press/hover-border affordance. Hover = the shared wash plus
    // the brightened border, so the whole tile reads as one press target.
    <Box
      as="button"
      direction="column"
      border
      rounded="lg"
      overflow="hidden"
      cursor="pointer"
      hoverBackground
      className={PRESET_CARD_HOVER}
      onClick={() => onSelect(preset)}
    >
      {/* Relative: the mockup positions absolutely inside this frame. */}
      <Box
        pointerEvents="none" height={compact ? 112 : 136}
        overflow="hidden"
        position="relative"
        fill="width"
      >
        <TilePreview preset={preset} color={color} />
      </Box>
      <Box
        direction="column"
        gap="xs"
        fill="width"
        // Taller footer band (the reference tile footer: ~72px content +
        // insets): one-line descriptions get the same air as two-line ones.
        minHeight={compact ? 88 : 100}
        paddingX={compact ? "md" : "lg"}
        paddingY="md"
      >
        <Text size="sm" weight="semibold" truncate={1}>
          {preset.label}
        </Text>
        <Text size="sm" variant="muted" truncate={2}>
          {preset.description}
        </Text>
      </Box>
    </Box>
  );
}

/**
 * The gallery pane: a scroll-spy sidebar of curated sections beside preset
 * tiles laid out in a three-column grid, filtered by a search that matches
 * label, description, section, and tags. Empty sections are pruned. Widgets
 * are authored in code; this is the single way to add one to a view.
 */
function GalleryTab({
  sidebarTitle,
  searchRef,
  onSelect,
  onClose,
}: {
  sidebarTitle: string;
  /** The dialog's `initialFocus` target — search takes focus on open. */
  searchRef: React.RefObject<HTMLInputElement | null>;
  onSelect: (preset: WidgetPreset) => void;
  onClose: () => void;
}) {
  const tier = useShellTier();
  const isMobile = tier === "compact";
  const registry = useWidgetRegistry();
  // The dashboard this gallery was opened over. It steers Featured and NOTHING
  // else: every section stays reachable on every dashboard, because scope
  // decides what is most USEFUL here, never what is permitted. Null off a
  // dashboard (the Replace picker in a detail dialog).
  const scopeKind = useDashboardScope()?.entity?.type;
  const [query, setQuery] = useState("");
  const [activeKey, setActiveKey] = useState<WidgetSection>(DEFAULT_SECTION);
  const scrollNodeRef = useRef<HTMLDivElement | null>(null);
  const sectionNodes = useRef<Map<WidgetSection, HTMLElement>>(new Map());
  const sectionRefSetters = useRef<Map<WidgetSection, (node: unknown) => void>>(new Map());
  const lockRef = useRef(false);
  /** The in-flight nav-jump's destination scrollTop — the arrival check in
   *  the scroll handler releases the lock when the animation reaches it. */
  const lockTargetRef = useRef<number | null>(null);
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const activeSection: GallerySection = { key: activeKey, ...WIDGET_SECTIONS[activeKey] };

  const visibleSections = useMemo(() => {
    const presets = registry.getPresets();
    const byKey = new Map(presets.map((p) => [p.key, p]));
    const q = query.trim().toLowerCase();
    // Search reads the section's LABEL, never its key — the key is an internal
    // slug ("embeds"), the label is the word on the nav row ("Embeds & apps").
    const matchesQuery = (p: WidgetPreset) =>
      !q ||
      p.label.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      (isWidgetSection(p.section) &&
        WIDGET_SECTIONS[p.section].label.toLowerCase().includes(q)) ||
      (p.tags ?? []).some((t) => (widgetTagLabel(t) ?? t).toLowerCase().includes(q));

    // Every preset files ITSELF. Its catalog `section` is the only thing that
    // decides where it lands, and it lands in exactly one place — so a widget
    // can neither go missing (the retired allowlist's failure) nor appear in
    // three sections at once (its other one). A preset naming a section this
    // build does not ship is skipped rather than inventing a nav row.
    const grouped = new Map<WidgetSection, WidgetPreset[]>();
    for (const preset of presets) {
      if (!isWidgetSection(preset.section)) continue;
      const bucket = grouped.get(preset.section);
      if (bucket) bucket.push(preset);
      else grouped.set(preset.section, [preset]);
    }
    // Featured is the one CURATED overlay, and the only place a preset shows
    // twice: it is a shortcut over the taxonomy, not a category in it. Its keys
    // follow the host scope, so a team board leads with people and a workflow
    // board leads with stage flow. A key with no live preset is skipped.
    grouped.set(
      "featured",
      featuredPresetKeys(scopeKind)
        .map((key) => byKey.get(key))
        .filter((preset): preset is WidgetPreset => Boolean(preset)),
    );

    return WIDGET_SECTION_ORDER.map((key) => ({
      section: { key, ...WIDGET_SECTIONS[key] } satisfies GallerySection,
      presets: orderSectionPresets(key, grouped.get(key) ?? []).filter(matchesQuery),
    })).filter(({ presets }) => presets.length > 0);
  }, [registry, query, scopeKind]);

  const getSectionRef = useCallback((key: WidgetSection) => {
    const cache = sectionRefSetters.current;
    let setter = cache.get(key);
    if (!setter) {
      setter = (node: unknown) => {
        const el = node as HTMLElement | null;
        if (el) sectionNodes.current.set(key, el);
        else sectionNodes.current.delete(key);
      };
      cache.set(key, setter);
    }
    return setter;
  }, []);

  const syncActiveSection = useCallback(() => {
    const container = scrollNodeRef.current;
    if (!container || lockRef.current) return;

    // No at-bottom special case: the LAST section stretches to a full
    // viewport (see the min-height on the last section wrapper), so every
    // section — the last included — genuinely reaches the activation line
    // and pure geometry decides. The old "bottom = last section" override
    // stole the highlight from a short second-to-last section whose landing
    // position coincided with the bottom of the range.
    const containerTop = container.getBoundingClientRect().top;
    let current =
      visibleSections[0]?.section.key ?? DEFAULT_SECTION;
    for (const { section } of visibleSections) {
      const el = sectionNodes.current.get(section.key);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= containerTop + ACTIVATION_OFFSET) {
        current = section.key;
      }
    }
    setActiveKey(current);
  }, [visibleSections]);

  const setScrollNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      scrollNodeRef.current = node;
      if (!node) return;

      let raf = 0;
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          // A nav-jump holds the spy locked on the CLICKED section for the
          // whole flight — re-reading mid-scroll positions would flash every
          // section the animation passes through the header and the nav.
          // The lock releases on ARRIVAL (scrollend is not universal, so
          // arrival is a distance check), and the jump's failsafe timer
          // covers an interrupted scroll that never arrives.
          if (lockRef.current && lockTargetRef.current !== null) {
            if (Math.abs(node.scrollTop - lockTargetRef.current) > 2) return;
            lockRef.current = false;
            lockTargetRef.current = null;
            if (lockTimer.current) clearTimeout(lockTimer.current);
            // Arrived exactly where the user asked — KEEP their pick rather
            // than re-deriving one from the landed position. A short section
            // near the end clamps to the bottom of the range, and a re-sync
            // there hits the at-bottom rule and hands the highlight to the
            // LAST section over the clicked one. The spy resumes on the next
            // real scroll.
            return;
          }
          syncActiveSection();
        });
      };

      node.addEventListener("scroll", onScroll, { passive: true });
      cleanupRef.current = () => {
        node.removeEventListener("scroll", onScroll);
        if (raf) cancelAnimationFrame(raf);
      };
    },
    [syncActiveSection],
  );

  const scrollToSection = useCallback(
    (key: WidgetSection) => {
      setActiveKey(key);
      const container = scrollNodeRef.current;
      const el = sectionNodes.current.get(key);
      if (!container || !el) return;

      // Land the section heading SECTION_SCROLL_INSET below the container
      // top — the same air the content column's own paddingTop gives the
      // first section at rest — instead of snapping it flush to the edge.
      // Clamped to the real scroll range: a bottom section can't reach the
      // inset position, and an unreachable target would never release the
      // arrival lock below.
      const top = Math.max(
        0,
        Math.min(
          el.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop -
            SECTION_SCROLL_INSET,
          container.scrollHeight - container.clientHeight,
        ),
      );
      // Nothing to animate (the clamped target IS the current position —
      // e.g. a short bottom section clicked while already at the bottom):
      // the click's setActiveKey above is the whole gesture. Locking here
      // would arm a lock no scroll event ever releases, and its failsafe
      // would re-derive from the landed position and steal the highlight.
      if (Math.abs(top - container.scrollTop) <= 2) return;
      // Lock the spy on the clicked section until the scroll ARRIVES (the
      // scroll handler's distance check) — a fixed short timer released
      // mid-flight and flashed the sections the animation passed through.
      // The long timer is only the failsafe for an interrupted scroll, and
      // it too releases WITHOUT re-deriving: the user's pick stands until
      // their next real scroll re-engages the spy.
      lockRef.current = true;
      lockTargetRef.current = top;
      if (lockTimer.current) clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(() => {
        lockRef.current = false;
        lockTargetRef.current = null;
      }, 1500);
      container.scrollTo({ top, behavior: "smooth" });
    },
    [],
  );

  useEffect(() => {
    if (visibleSections.some(({ section }) => section.key === activeKey)) return;
    setActiveKey(visibleSections[0]?.section.key ?? DEFAULT_SECTION);
  }, [activeKey, visibleSections]);

  useEffect(
    () => () => {
      if (cleanupRef.current) cleanupRef.current();
      if (lockTimer.current) clearTimeout(lockTimer.current);
    },
    [],
  );

  // The search field — one control in both frames. Desktop seats it in the
  // content header against the close; the phone sheet gives it a full-width
  // band under the title.
  const search = (
    <InputGroup>
      <InputGroupAddon>
        <Icon name="search" />
      </InputGroupAddon>
      <InputGroupInput
        ref={searchRef}
        type="search"
        placeholder="Search..."
        value={query}
        onValueChange={setQuery}
      />
    </InputGroup>
  );

  const empty =
    visibleSections.length === 0 ? (
      <Box paddingY="xl">
        <EmptyState
          size="sm"
          media="circle"
          icon="search-x"
          title="No widgets found"
          description={
            query ? `Nothing matches "${query}"` : "There are no widgets to add yet"
          }
          actions={
            query ? (
              <Button size="sm" variant="outline" onClick={() => setQuery("")}>
                Clear search
              </Button>
            ) : undefined
          }
        />
      </Box>
    ) : null;

  // The chapters: one heading + tile grid per section, in both frames. The
  // scroll-spy (section refs, the last section's full-scrollport stretch)
  // only serves the desktop nav; the phone reads the same chapters as one
  // continuous scroll with no nav to keep in step.
  const chapters = visibleSections.map(({ section, presets }, sectionIndex) => (
    <Box
      key={section.key}
      direction="column"
      ref={isMobile ? undefined : getSectionRef(section.key)}
      gap="md"
      // The LAST section stretches to a full scrollport (100cqh minus the
      // 16px landing inset): every section — the last included — can
      // genuinely rest at the activation line, so the spy needs no
      // "bottom = last section" override, and a short second-to-last
      // section (Activity) keeps the highlight after its jump instead of
      // losing it to the bottom rule.
      className={
        !isMobile && sectionIndex === visibleSections.length - 1
          ? "min-h-[calc(100cqh-1rem)]"
          : undefined
      }
    >
      {/* A real heading on the 20px rung (the reference group-title
          metrics) — the section names are the page's outline; no subtext,
          the tiles' own descriptions carry the detail. */}
      <Text as="h3" size={isMobile ? "lg" : "xl"} weight="semibold">
        {section.label}
      </Text>
      <Box gap={isMobile ? "sm" : "md"} className={PRESET_GRID}>
        {presets.map((preset, index) => (
          <PresetEntry
            key={`${section.key}-${preset.key}`}
            preset={preset}
            color={
              section.key === "custom"
                ? at(CUSTOM_PRESET_HUES, index % CUSTOM_PRESET_HUES.length)
                : section.color
            }
            onSelect={onSelect}
            compact={isMobile}
          />
        ))}
      </Box>
    </Box>
  ));

  if (isMobile) {
    // The phone sheet: no nav pane — a labeled nav beside the tile column
    // left neither enough width, and the icon rail it collapsed to was a
    // row of unlabeled glyphs nobody could read. The sheet's own header
    // grammar carries the identity (title on the drag-bar row, like the App
    // Center and the shortcuts reference), the search takes a full-width
    // band under it, and the chapters run as ONE continuous scroll in the
    // sheet's panel — the section headings are the outline. Dismissal is
    // the sheet's: the bar, a swipe, a tap outside.
    return (
      <>
        <DialogHeader>
          <DialogTitle>{sidebarTitle}</DialogTitle>
        </DialogHeader>
        <Box paddingBottom="sm" paddingX="lg" shrink={false}>
          {search}
        </Box>
        <DialogPanel>
          <Box direction="column" gap="xl" paddingBottom="md">
            {empty}
            {chapters}
          </Box>
        </DialogPanel>
      </>
    );
  }

  return (
    <>
      <DialogTitle visuallyHidden>{sidebarTitle}</DialogTitle>
      <Box direction="row" fill minHeight={0}>
        {/* Left nav — the manage-dialog NavPane: it owns the dialog identity
            (tile + title) at the top, aligned on the content header's 48px
            rung, over the scroll-spy section rows. On a tablet it keeps its
            labels on a narrower column so the tiles get the difference. */}
        <NavPane width={tier === "medium" ? NAV_WIDTH_NARROW : undefined}>
          <NavPaneHeader>
            {/* Neutral ink on purpose — an identity mark, not a themed
                accent. */}
            <IconTile bordered name="layout-dashboard" size="lg" />
            <NavPaneTitle>{sidebarTitle}</NavPaneTitle>
          </NavPaneHeader>
          <NavPaneContent>
            <NavPaneGroup>
              {/* Rows mirror the CONTENT's pruned section list — a section
                  with nothing to show (no live presets, or search-filtered
                  empty) gets no nav row either. */}
              {visibleSections.map(({ section }) => (
                <NavPaneItem
                  key={section.key}
                  isActive={section.key === activeKey}
                  onClick={() => scrollToSection(section.key)}
                >
                  <Icon name={section.icon} />
                  {section.label}
                </NavPaneItem>
              ))}
            </NavPaneGroup>
            {/* -mx-2 bleeds the hairline through the pane's 8px gutter to the
                pane edges — the separator is a pane divider, not a row. */}
            <Box paddingY="sm" className="-mx-2">
              <Separator />
            </Box>
            {/* Resource links (the reference help-docs / webinar cluster).
                Disabled until the docs exist — the slot is the point. */}
            <NavPaneGroup>
              <NavPaneItem disabled>
                <Icon name="file-text" />
                Help Docs
              </NavPaneItem>
            </NavPaneGroup>
          </NavPaneContent>
        </NavPane>

        <Box direction="column" fill minWidth={0}>
          {/* The content header names WHERE you are — the scroll-spy's active
              section; the dialog identity lives in the sidebar header. Search
              sits against the close pill (the Integrations grammar). */}
          <ManageDialogHeader onClose={onClose}>
            <Box direction="row" fill="flex" align="center" gap="sm" minWidth={0}>
              <Icon name={activeSection.icon} size="sm" color="muted" />
              <Text size="sm" weight="semibold" truncate={1}>
                {activeSection.label}
              </Text>
            </Box>
            <Box shrink width={300}>
              {search}
            </Box>
          </ManageDialogHeader>
          {/* Plain overflow with a ref, not ScrollArea: the scroll-spy reads
              `scrollTop` off this element and `scrollToSection` scrolls it
              directly. container-type:size names it the query container the
              last section's full-viewport min-height (100cqh) measures — its
              own size is flex-determined, so size containment is safe. */}
          <Box
            ref={setScrollNode}
            fill="flex"
            overflow="auto"
            className="[container-type:size]"
          >
            <Box
              direction="column"
              // 48px between sections: each section is its own chapter, and
              // the tighter 24px read as one continuous list under the h3
              // headings.
              gap="2xl"
              paddingX="lg"
              paddingTop="md"
              // 96px overscroll gutter (the token scale tops out at 48): the
              // last section can ride well clear of the popup edge.
              className="pb-24"
            >
              {empty}
              {chapters}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

/**
 * Add Widget — the curated preset gallery. Widgets are authored in code; this
 * is the single way to add one to a view.
 */
export function AddWidgetDialog({
  open,
  onOpenChange,
  onSelect,
  title = "Add Widget",
}: AddWidgetDialogProps) {
  // Open lands focus in the header search — type-to-filter immediately
  // (Spencer, 2026-08-21; same contract as the Integrations dialog).
  const searchRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      // The phone sheet: a work surface takes the full height.
      sheetSize="full"
    >
      {/* The window tier — the gallery floats as a bounded window over the
       * board it is adding to, like the App Center over the app. */}
      <DialogPopup
        initialFocus={searchRef}
        size="window"
        // The manage-dialog room rests on the page fill: its nav pane lifts
        // above it and its tables read on the surface they read on everywhere.
        surface="background"
        showCloseButton={false}
      >
        <GalleryTab
          sidebarTitle={title}
          searchRef={searchRef}
          onSelect={(p) => {
            onSelect(p);
            onOpenChange(false);
          }}
          onClose={() => onOpenChange(false)}
        />
      </DialogPopup>
    </Dialog>
  );
}
